import { useEffect, useState, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { api } from "@/lib/api";
import { useBilling } from "@/context/BillingContext";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";

const MAX_POLLS = 8;

export default function PaymentSuccess() {
  const navigate = useNavigate();
  const { refreshBilling } = useBilling();
  const [state, setState] = useState("checking"); // checking | success | error | timeout
  const polled = useRef(false);

  useEffect(() => {
    if (polled.current) return;
    polled.current = true;
    const sessionId = new URLSearchParams(window.location.search).get("session_id");
    if (!sessionId) {
      setState("error");
      return;
    }
    let tries = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          await refreshBilling();
          setState("success");
          return;
        }
        if (data.status === "expired" || data.payment_status === "failed") {
          setState("error");
          return;
        }
      } catch {
        /* keep trying */
      }
      tries += 1;
      if (tries >= MAX_POLLS) {
        setState("timeout");
        return;
      }
      setTimeout(poll, 2000);
    };
    poll();
  }, [refreshBilling]);

  return (
    <div className="min-h-screen bp-grid">
      <Navbar />
      <div className="mx-auto flex max-w-md flex-col items-center px-4 py-28 text-center" data-testid="payment-success-page">
        {state === "checking" && (
          <>
            <Loader2 className="h-10 w-10 animate-spin text-primary" />
            <p className="mt-4 font-mono2 text-xs uppercase tracking-widest text-muted-foreground">Confirming your payment…</p>
          </>
        )}
        {state === "success" && (
          <>
            <CheckCircle2 className="h-14 w-14 text-primary" />
            <h1 className="mt-5 font-heading text-3xl font-extrabold tracking-tighter">You're all set</h1>
            <p className="mt-2 text-muted-foreground">Your purchase is confirmed and your account has been upgraded.</p>
            <Button onClick={() => navigate("/dashboard")} data-testid="success-go-dashboard"
              className="mt-6 rounded-sm bg-primary font-mono2 uppercase tracking-widest text-black hover:bg-primary/90">
              Go to Projects
            </Button>
          </>
        )}
        {(state === "error" || state === "timeout") && (
          <>
            <XCircle className="h-14 w-14 text-destructive" />
            <h1 className="mt-5 font-heading text-2xl font-bold tracking-tight">
              {state === "timeout" ? "Still processing" : "Payment not completed"}
            </h1>
            <p className="mt-2 text-muted-foreground">
              {state === "timeout"
                ? "This is taking longer than usual. Your plan will update automatically once confirmed."
                : "We couldn't confirm your payment. No charge was applied if it didn't complete."}
            </p>
            <Link to="/billing" className="mt-6" data-testid="back-to-billing">
              <Button variant="outline" className="rounded-sm border-border font-mono2 uppercase tracking-widest">Back to Plans</Button>
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
